import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  IconButton, Chip, Tooltip, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, FormControl,
  InputLabel, Select, MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Search, Plus, Edit2, Trash2, Unlock, Phone, X } from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";

import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

const TYPES = ["", "DID", "TollFree", "Local", "International"];

const EMPTY_FORM = { number: "", type: "DID", description: "", carrier: "" };

function PhoneNumbersAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [rowsPerPage]         = useState(15);
  const [search,  setSearch]  = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // dialogs
  const [addOpen,    setAddOpen]    = useState(false);
  const [editOpen,   setEditOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [dlgError,   setDlgError]   = useState("");

  const fetchNumbers = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      page: page + 1,
      size: rowsPerPage,
      ...(search     ? { q: search }        : {}),
      ...(typeFilter ? { type: typeFilter }  : {}),
    });
    axiosInstance.get(`/admin/phone-numbers?${params}`)
      .then(r => { setRows(r.data.numbers || []); setTotal(r.data.total || 0); })
      .catch(e => setError(e.response?.data?.message || t("failedLoadPhoneNumbers")))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, search, typeFilter]);

  useEffect(() => { fetchNumbers(); }, [fetchNumbers]);

  const handleAdd = async () => {
    setSaving(true); setDlgError("");
    try {
      await axiosInstance.post("/admin/phone-numbers", form);
      setAddOpen(false); setForm(EMPTY_FORM); fetchNumbers();
    } catch (e) {
      setDlgError(e.response?.data?.message || t("failedAddNumber"));
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true); setDlgError("");
    try {
      await axiosInstance.patch(`/admin/phone-numbers/${selected.id}`, form);
      setEditOpen(false); setSelected(null); setForm(EMPTY_FORM); fetchNumbers();
    } catch (e) {
      setDlgError(e.response?.data?.message || t("failedUpdateNumber"));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true); setDlgError("");
    try {
      await axiosInstance.delete(`/admin/phone-numbers/${selected.id}`);
      setDeleteOpen(false); setSelected(null); fetchNumbers();
    } catch (e) {
      setDlgError(e.response?.data?.message || t("failedDeleteNumber"));
    } finally { setSaving(false); }
  };

  const handleRelease = async () => {
    setSaving(true); setDlgError("");
    try {
      await axiosInstance.patch(`/admin/phone-numbers/${selected.id}`, { releaseReservation: true });
      setReleaseOpen(false); setSelected(null); fetchNumbers();
    } catch (e) {
      setDlgError(e.response?.data?.message || t("failedReleaseReservation"));
    } finally { setSaving(false); }
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm({ number: row.number, type: row.type || "DID", description: row.description || "", carrier: row.carrier || "" });
    setDlgError(""); setEditOpen(true);
  };

  const glassSx = { background: "var(--surface-card)", border: "1px solid var(--line-200)", boxShadow: "var(--shadow-card)", borderRadius: "18px" };

  const cellSx = { fontSize: "0.8125rem", borderColor: "var(--line-200)", py: 1.25 };
  const headSx = { ...cellSx, fontWeight: 700, color: "text.secondary", background: isLight ? alpha("#000", 0.025) : alpha("#fff", 0.025) };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>{t("phoneNumbersTitle")}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("phoneNumbersSub")}</Typography>
        </Box>
        <Button
          onClick={() => { setForm(EMPTY_FORM); setDlgError(""); setAddOpen(true); }}
          startIcon={<Plus size={15} />}
          variant="contained"
          size="small"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "0.8125rem", background: "#DC2626", "&:hover": { background: "#B91C1C" }, boxShadow: "0 2px 8px rgba(220,38,38,0.3)", textTransform: "none", px: 2 }}
        >
          {t("addNumber")}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
        <TextField
          placeholder={t("searchNumberDesc")}
          size="small"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" } }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: "0.875rem" }}>{t("typeLabel")}</InputLabel>
          <Select value={typeFilter} label={t("typeLabel")} onChange={e => { setTypeFilter(e.target.value); setPage(0); }} sx={{ borderRadius: "10px", fontSize: "0.875rem" }}>
            {TYPES.map(tp => <MenuItem key={tp} value={tp} sx={{ fontSize: "0.875rem" }}>{tp || t("allTypes")}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Box sx={{ ...glassSx, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>{t("colNumber")}</TableCell>
                <TableCell sx={headSx}>{t("colType")}</TableCell>
                <TableCell sx={headSx}>{t("colStatus")}</TableCell>
                <TableCell sx={headSx}>{t("colReservedBy")}</TableCell>
                <TableCell sx={headSx}>{t("colDescription")}</TableCell>
                <TableCell sx={{ ...headSx, textAlign: "right" }}>{t("colActions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "text.secondary", fontSize: "0.875rem" }}>{t("noPhoneNumbersFound")}</TableCell></TableRow>
              ) : rows.map(row => (
                <TableRow key={row.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Phone size={14} color="#6366F1" />
                      <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums" }}>{row.number}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Chip label={row.type || "DID"} size="small" sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600, background: alpha("#6366F1", 0.08), color: "#6366F1", border: "none" }} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {row.isReserved ? (
                      <Chip label={t("chipReserved")} size="small" sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600, background: alpha("#F59E0B", 0.1), color: "#F59E0B", border: "none" }} />
                    ) : (
                      <Chip label={t("chipAvailable")} size="small" sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600, background: alpha("#22C55E", 0.1), color: "#22C55E", border: "none" }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: "text.secondary" }}>{row.reservedByEmail || "—"}</TableCell>
                  <TableCell sx={{ ...cellSx, color: "text.secondary", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.description || "—"}</TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <Tooltip title={t("tooltipEdit")}><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#6366F1", "&:hover": { background: alpha("#6366F1", 0.08) }, borderRadius: "8px", width: 28, height: 28 }}><Edit2 size={13} /></IconButton></Tooltip>
                      {row.isReserved && (
                        <Tooltip title={t("tooltipReleaseReservation")}><IconButton size="small" onClick={() => { setSelected(row); setDlgError(""); setReleaseOpen(true); }} sx={{ color: "#F59E0B", "&:hover": { background: alpha("#F59E0B", 0.08) }, borderRadius: "8px", width: 28, height: 28 }}><Unlock size={13} /></IconButton></Tooltip>
                      )}
                      <Tooltip title={t("tooltipDelete")}><IconButton size="small" onClick={() => { setSelected(row); setDlgError(""); setDeleteOpen(true); }} sx={{ color: "#EF4444", "&:hover": { background: alpha("#EF4444", 0.08) }, borderRadius: "8px", width: 28, height: 28 }}><Trash2 size={13} /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[15]}
          onPageChange={(_, p) => setPage(p)}
          sx={{ borderTop: "1px solid var(--line-200)", "& .MuiTablePagination-displayedRows": { fontSize: "0.8125rem" } }}
        />
      </Box>

      {/* Add Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {t("addPhoneNumberTitle")}
          <IconButton size="small" onClick={() => setAddOpen(false)}><X size={16} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          {dlgError && <Alert severity="error" sx={{ borderRadius: 2 }}>{dlgError}</Alert>}
          <TextField label={t("phoneNumberLabel")} value={form.number} onChange={e => setForm(f => ({ ...f, number: e.target.value }))} size="small" fullWidth InputProps={{ sx: { borderRadius: "10px" } }} />
          <FormControl size="small" fullWidth>
            <InputLabel>{t("typeLabel")}</InputLabel>
            <Select value={form.type} label={t("typeLabel")} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} sx={{ borderRadius: "10px" }}>
              {TYPES.filter(Boolean).map(tp => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label={t("descriptionOptional")} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} size="small" fullWidth multiline rows={2} InputProps={{ sx: { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>{t("cancel")}</Button>
          <Button onClick={handleAdd} disabled={saving || !form.number.trim()} variant="contained" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, background: "#DC2626", "&:hover": { background: "#B91C1C" } }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : t("add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {t("editPhoneNumberTitle")}
          <IconButton size="small" onClick={() => setEditOpen(false)}><X size={16} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          {dlgError && <Alert severity="error" sx={{ borderRadius: 2 }}>{dlgError}</Alert>}
          <TextField label={t("phoneNumberLabel")} value={form.number} onChange={e => setForm(f => ({ ...f, number: e.target.value }))} size="small" fullWidth InputProps={{ sx: { borderRadius: "10px" } }} />
          <FormControl size="small" fullWidth>
            <InputLabel>{t("typeLabel")}</InputLabel>
            <Select value={form.type} label={t("typeLabel")} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} sx={{ borderRadius: "10px" }}>
              {TYPES.filter(Boolean).map(tp => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label={t("descriptionOptional")} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} size="small" fullWidth multiline rows={2} InputProps={{ sx: { borderRadius: "10px" } }} />
          <TextField label={t("carrierLabel")} value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))} size="small" fullWidth placeholder="telnyx · zain_jo" helperText={t("carrierHelp")} InputProps={{ sx: { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>{t("cancel")}</Button>
          <Button onClick={handleEdit} disabled={saving || !form.number.trim()} variant="contained" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, background: "#DC2626", "&:hover": { background: "#B91C1C" } }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Dialog */}
      <Dialog open={releaseOpen} onClose={() => setReleaseOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>{t("releaseReservationTitle")}</DialogTitle>
        <DialogContent>
          {dlgError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{dlgError}</Alert>}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("releaseReservationConfirm", { number: selected?.number })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setReleaseOpen(false)} sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>{t("cancel")}</Button>
          <Button onClick={handleRelease} disabled={saving} variant="contained" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, background: "#F59E0B", color: "#fff", "&:hover": { background: "#D97706" } }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : t("release")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#EF4444" }}>{t("deletePhoneNumberTitle")}</DialogTitle>
        <DialogContent>
          {dlgError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{dlgError}</Alert>}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("deleteNumberConfirm", { number: selected?.number })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ borderRadius: "10px", textTransform: "none", color: "text.secondary" }}>{t("cancel")}</Button>
          <Button onClick={handleDelete} disabled={saving} variant="contained" color="error" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PhoneNumbersAdmin;
